import { Type, Ratio, Clock, Palette, Mic, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { FontPicker } from "../FontPicker";
import { ColorControl, SectionTitle } from "../StudioControls";
import { RATIO_OPTIONS } from "@/lib/constants";
import { MediaInput } from "./MediaInput";
import { DurationPicker } from "./DurationPicker";
import type { AppConfig } from "@/lib/types";

interface ClassicModeUIProps {
  config: AppConfig;
  onChange: (config: AppConfig) => void;
  handleAudioUpload: (type: 'voiceover' | 'music', url: string) => void;
}

export function ClassicModeUI({ config, onChange, handleAudioUpload }: ClassicModeUIProps) {
  return (
    <div className="flex flex-col gap-6 pb-10">
      <section className="control-section">
        <SectionTitle icon={Type} title="Script" />
        <Textarea
          placeholder="Paste your poetic text here... Every space and line break will be preserved exactly as you see it."
          className="studio-textarea min-h-64"
          value={config.text}
          onChange={(e) => onChange({ ...config, text: e.target.value })}
        />
      </section>

      <div className="grid gap-5 md:grid-cols-2">
        <section className="control-section">
          <SectionTitle icon={Ratio} title="Canvas" />
          <div className="ratio-grid">
            {RATIO_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={cn("ratio-option", config.aspectRatio === opt.value && "is-active")}
                onClick={() => onChange({ ...config, aspectRatio: opt.value })}
              >
                <div className={cn("ratio-shape", opt.shape)} />
                <span className="ratio-label">{opt.label}</span>
                <span className="ratio-value">{opt.value}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="control-section">
          <SectionTitle icon={Clock} title="Duration" />
          <div className="field-stack">
            <DurationPicker
              value={config.durationStr}
              onChange={(val) => onChange({ ...config, durationStr: val })}
            />
            <p className="px-1 text-[10px] leading-relaxed text-muted-foreground mt-1">
              {config.audio.musicUrl || config.audio.voiceoverUrl
                ? "Duration is overridden by audio length."
                : "Total animation time from top to bottom."}
            </p>
          </div>
        </section>
      </div>

      <section className="control-section">
        <SectionTitle icon={Palette} title="Styling" />
        <div className="grid gap-4 md:grid-cols-2">
          <ColorControl
            label="Text Color"
            value={config.textColor}
            onChange={(value) => onChange({ ...config, textColor: value })}
          />
          <ColorControl
            label="Background Color"
            value={config.backgroundColor}
            onChange={(value) => onChange({ ...config, backgroundColor: value })}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2 mt-4 border-t border-border/40 pt-4">
          <div className="field-stack">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Font</Label>
            <FontPicker
              value={config.fontName}
              onChange={(font) => onChange({ ...config, fontName: font })}
            />
          </div>
          <div className="field-stack">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Text Size</Label>
              <span className="text-[10px] font-bold text-primary">{config.fontSize}px</span>
            </div>
            <input
              type="range"
              min="12"
              max="120"
              step="1"
              className="studio-slider"
              value={config.fontSize}
              onChange={(e) => onChange({ ...config, fontSize: Number(e.target.value) })}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 mt-4">
          <div className="field-stack">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Alignment</Label>
            <div className="flex gap-1 bg-input/30 p-1 rounded-md border border-border/50">
              {(['left', 'center', 'right'] as const).map((align) => (
                <button
                  key={align}
                  onClick={() => onChange({ ...config, textAlign: align })}
                  className={cn(
                    "flex-1 py-1 text-[10px] font-bold uppercase rounded transition-all",
                    config.textAlign === align ? "bg-primary text-primary-foreground shadow-lg" : "text-muted-foreground hover:bg-white/5"
                  )}
                >
                  {align}
                </button>
              ))}
            </div>
          </div>
          <div className="field-stack">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Line Height</Label>
              <span className="text-[10px] font-bold text-primary">{config.lineHeight.toFixed(1)}</span>
            </div>
            <input
              type="range"
              min="0.5"
              max="3"
              step="0.1"
              className="studio-slider"
              value={config.lineHeight}
              onChange={(e) => onChange({ ...config, lineHeight: Number(e.target.value) })}
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="field-stack">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Text Shadow (Glow)</Label>
              <span className="text-[10px] font-bold text-primary">{config.textShadow}px</span>
            </div>
            <input
              type="range"
              min="0"
              max="40"
              step="1"
              className="studio-slider"
              value={config.textShadow}
              onChange={(e) => onChange({ ...config, textShadow: Number(e.target.value) })}
            />
          </div>
        </div>
      </section>

      <section className="control-section">
        <SectionTitle icon={Mic} title="Audio" />
        <div className="grid gap-4 md:grid-cols-2">
          <MediaInput
            id="voiceover"
            label="Voiceover Track"
            value={config.audio.voiceoverUrl || ''}
            onChange={(url) => handleAudioUpload('voiceover', url)}
            accept="audio/*,.aac,video/*"
          />
          <MediaInput
            id="music"
            label="Music Track"
            value={config.audio.musicUrl || ''}
            onChange={(url) => handleAudioUpload('music', url)}
            accept="audio/*,.aac,video/*"
          />
        </div>
      </section>

      <section className="control-section">
        <SectionTitle icon={ImageIcon} title="Backdrop" />
        <div className="grid gap-4 md:grid-cols-2">
          <MediaInput
            id="backgroundImage"
            label="Background Image"
            value={config.backgroundImage}
            onChange={(value) => onChange({ ...config, backgroundImage: value })}
            accept="image/*"
          />
          <MediaInput
            id="backgroundVideo"
            label="Background Video"
            value={config.backgroundVideoUrl}
            onChange={(value) => onChange({ ...config, backgroundVideoUrl: value })}
            accept="video/*"
          />
        </div>

        <div className="mt-2 grid gap-6 border-t border-border/40 pt-4 md:grid-cols-2">
          <div className="field-stack">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Soft Blur</Label>
              <span className="text-[10px] font-bold text-primary">{config.blur}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              className="studio-slider"
              value={config.blur}
              onChange={(e) => onChange({ ...config, blur: Number(e.target.value) })}
            />
          </div>
          <div className="field-stack">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Vignette</Label>
              <span className="text-[10px] font-bold text-primary">{config.vignette}%</span>
            </div>
            <input
              type="range"
              min="-100"
              max="100"
              step="1"
              className="studio-slider"
              value={config.vignette}
              onChange={(e) => onChange({ ...config, vignette: Number(e.target.value) })}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
